import { useEffect, useRef } from "react";

import { uploadSnapshot } from "@/lib/api";

import { holdSnapshotSchedule, type SnapshotConfig } from "../camera";

/**
 * EF-8 — hold the attempt's ONE snapshot schedule from a page on which the
 * test is RUNNING but no camera monitor is mounted: Review, and SectionReview
 * outside a break. The question page holds it through its monitor
 * (useAntiCheat); this is the same hold without a detector, a controller or
 * any activity recording — nothing is recorded here but the scheduled photos
 * the consent describes ("one every 30 seconds while the test runs").
 *
 * `active` false (a break) releases it: the schedule pauses keeping its
 * place and the next hold — "Begin Section N" → Question — resumes it without
 * a burst unless a photo was due. `config` is the schedule the attempt froze
 * (the tab's CandidateMeta; GET /review does not carry it); nothing is held
 * until it is known.
 */
export function useSnapshotSchedule(
  token: string,
  attemptId: string | null,
  config: SnapshotConfig | null | undefined,
  active: boolean,
): void {
  const configRef = useRef(config);
  configRef.current = config;
  const known = Boolean(config);
  useEffect(() => {
    if (!attemptId || !active || !known) return;
    return holdSnapshotSchedule({
      attemptId,
      getConfig: () => configRef.current ?? null,
      upload: (blob, trigger, capturedAt, seq) =>
        uploadSnapshot(token, attemptId, blob, trigger, capturedAt, seq),
    });
  }, [token, attemptId, active, known]);
}
